import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";
import React, { useEffect, useRef, useCallback, useState } from "react";
import { DragControls } from "three-stdlib";
import HorizontalPlane from "../threeDComponent/Helpers/HorizontalPlane";
import VerticalLine from "../threeDComponent/Helpers/VerticalLine";
import { colors } from "../../utils/colors";
import { useScaleSizeUniformly } from "./hooks";
import { useOrbit } from "../CustomHooks";

function CustomDragHandles({ position, onPositionChange, meshRef }) {
  const xyHandleRefInvisible = useRef();
  const xyHandleRefVisible = useRef();
  const zHandleRefInvisible = useRef();
  const zHandleRefVisible = useRef();

  const { camera, gl, setFrameloop } = useThree();
  const isDragging = useRef(false);
  const { disableOrbit, enableOrbit } = useOrbit();
  const [scaleFactor, setScaleFactor] = useState(1);

  useScaleSizeUniformly({
    referenceObjectRef: meshRef,
    active: !isDragging.current,
    setScaleFactorFn: setScaleFactor,
  });
  useScaleSizeUniformly({
    referenceObjectRef: meshRef,
    scaleObjectRef: xyHandleRefVisible,
  });
  useScaleSizeUniformly({
    referenceObjectRef: meshRef,
    scaleObjectRef: zHandleRefVisible,
  });

  const getHandleOffset = useCallback(
    (axis) => {
      if (!meshRef?.current) return 4;
      const box = new THREE.Box3().setFromObject(meshRef.current);
      const size = box.getSize(new THREE.Vector3());

      const ySize = Math.max(scaleFactor * 3 * 0.5 + 3, size.y * 0.5 + 2);
      const xSize = Math.max(scaleFactor * 5 * 0.5 + 7, size.x * 0.25 + 2);
      const zSize = Math.max(scaleFactor * 5 * 0.5 + 7, size.z * 0.25 + 2);
      return axis === "y" ? ySize : axis === "x" ? xSize : zSize;
    },
    [meshRef, scaleFactor]
  );

  const getHandlePosition = useCallback(
    (objectPosition, axis) => {
      return axis === "y"
        ? [
            objectPosition[0],
            objectPosition[1] + getHandleOffset("y"),
            objectPosition[2],
          ]
        : [
            objectPosition[0] + getHandleOffset("x"),
            objectPosition[1],
            objectPosition[2] + getHandleOffset("z"),
          ];
    },
    [getHandleOffset]
  );

  const getObjectPosition = useCallback(
    (handlePosition, startPosition, axis) => {
      const yHandleOffset = getHandleOffset("y");
      const xHandleOffset = getHandleOffset("x");
      const zHandleOffset = getHandleOffset("z");

      return axis === "y"
        ? [
            startPosition?.x,
            handlePosition?.y - yHandleOffset,
            startPosition?.z,
          ]
        : [
            handlePosition?.x - xHandleOffset,
            startPosition?.y,
            handlePosition?.z - zHandleOffset,
          ];
    },
    [getHandleOffset]
  );

  const createDragHandleControls = useCallback(
    (handleRef, axis) => {
      if (!handleRef.current) return null;

      const controls = new DragControls(
        [handleRef.current],
        camera,
        gl.domElement
      );

      let startPosition = null;
      const handleDragStart = (e) => {
        isDragging.current = true;

        disableOrbit();

        const pos = e.object.position;
        startPosition = { x: pos.x, y: pos.y, z: pos.z };
        setFrameloop("always");
        gl.domElement.style.cursor = "grabbing";
      };

      const handleDrag = (e) => {
        if (!isDragging.current || !startPosition) return;

        const pos = e.object.position;

        const newPosition = getObjectPosition(pos, startPosition, axis);
        if (onPositionChange) {
          onPositionChange(newPosition);
        }
      };

      const handleDragEnd = (e) => {
        isDragging.current = false;
        startPosition = null;

        enableOrbit();

        setFrameloop("demand");
        gl.domElement.style.cursor = "auto";
      };

      controls.addEventListener("dragstart", handleDragStart);
      controls.addEventListener("drag", handleDrag);
      controls.addEventListener("dragend", handleDragEnd);

      return controls;
    },
    [
      camera,
      gl,
      setFrameloop,
      onPositionChange,
      getObjectPosition,
      disableOrbit,
      enableOrbit,
    ]
  );

  useEffect(() => {
    const dragControls = createDragHandleControls(xyHandleRefInvisible, "xz");
    return () => dragControls.dispose();
  }, [createDragHandleControls, xyHandleRefInvisible]);

  useEffect(() => {
    const dragControls = createDragHandleControls(zHandleRefInvisible, "y");
    return () => dragControls.dispose();
  }, [createDragHandleControls, zHandleRefInvisible]);

  useFrame(() => {
    if (xyHandleRefInvisible.current && position) {
      const handlePosition = getHandlePosition(position, "xz");
      xyHandleRefInvisible.current.position.set(
        handlePosition[0],
        handlePosition[1],
        handlePosition[2]
      );
    }
    if (zHandleRefInvisible.current && position) {
      const handlePosition = getHandlePosition(position, "y");
      zHandleRefInvisible.current.position.set(
        handlePosition[0],
        handlePosition[1],
        handlePosition[2]
      );
    }
  });

  const arrowsCommonArgs = [
    new THREE.Vector3(0, 0, 0),
    1,
    colors.red500(),
    0.45,
    0.6,
  ];

  const xyHandlePosition = getHandlePosition(position, "xz");
  const zHandlePosition = getHandlePosition(position, "y");

  return (
    <group>
      <mesh ref={xyHandleRefInvisible}>
        <sphereGeometry args={[1]} />
        <meshBasicMaterial transparent opacity={0.0} />
      </mesh>

      <mesh
        position={[
          xyHandlePosition[0],
          xyHandlePosition[1],
          xyHandlePosition[2],
        ]}
        ref={xyHandleRefVisible}
      >
        <arrowHelper args={[new THREE.Vector3(1, 0, 0), ...arrowsCommonArgs]} />
        <arrowHelper args={[new THREE.Vector3(0, 0, 1), ...arrowsCommonArgs]} />
        <arrowHelper
          args={[new THREE.Vector3(-1, 0, 0), ...arrowsCommonArgs]}
        />
        <arrowHelper
          args={[new THREE.Vector3(0, 0, -1), ...arrowsCommonArgs]}
        />
      </mesh>

      <mesh ref={zHandleRefInvisible}>
        <sphereGeometry args={[1]} />
        <meshBasicMaterial transparent opacity={0.0} />
      </mesh>
      <mesh
        position={[zHandlePosition[0], zHandlePosition[1], zHandlePosition[2]]}
        ref={zHandleRefVisible}
      >
        <arrowHelper args={[new THREE.Vector3(0, 1, 0), ...arrowsCommonArgs]} />
        <arrowHelper
          args={[new THREE.Vector3(0, -1, 0), ...arrowsCommonArgs]}
        />
      </mesh>

      <HorizontalPlane
        targetRef={meshRef}
        zLength={
          Math.abs((xyHandlePosition?.[2] ?? 0) - (position[2] ?? 0)) * 2
        }
        xLength={
          Math.abs((xyHandlePosition?.[0] ?? 0) - (position[0] ?? 0)) * 2
        }
        color={colors.red500()}
      />
      <VerticalLine
        targetRef={meshRef}
        lowerLength={500}
        upperLength={(zHandlePosition?.[1] ?? 0) - (position[1] ?? 0)}
        color={colors.red500()}
      />
    </group>
  );
}

export default CustomDragHandles;
